import React, { useCallback, useMemo, useRef, useState } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from 'components/hooks/useToast';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { CustomHeaderCell } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CustomHeaderCell';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { Plus } from 'assets/assetsV1/iconComponents';

import { ANSWER_TYPES, QUESTION_TYPES } from 'constants/ProjectConstants';
import { getQuestionDetailsRoute } from 'constants/helper';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { showQuestionDetails } from 'services/questionDetails';
import { saveQuestion } from 'services/reviews';

import { AgGridSearchBar } from 'uiToolkitV1/AgGridSearchBar';
import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';
import { Button } from 'uiToolkitV1/Button';
import { ChipSelect } from 'uiToolkitV1/ChipSelect';
import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { useGetActiveQuestionsQuery, useGetArchiveQuestionsQuery, useGetSampleQuestionsQuery } from './Hooks/dataHooks';
import AnswerType from './components/AnswerType';
import QuestionType from './components/QuestionType';
import { generateReviewQuestionBankOptions } from './utils';

const ReviewQuestionBankV1 = () => {
  const { data: activeQuestions = [], isLoading } = useGetActiveQuestionsQuery();
  const { data: archivedQuestions = [] } = useGetArchiveQuestionsQuery();
  const { data: sampleQuestions = [] } = useGetSampleQuestionsQuery();
  const [selectedQuestionId, setSelectedQuestionId] = useState('active');
  const gridRef = useRef();
  const history = useHistory();
  const queryClient = useQueryClient();
  const toast = useToast();
  const dispatch = useDispatch();

  let rowData;

  if (selectedQuestionId === 'active') {
    rowData = activeQuestions;
  } else if (selectedQuestionId === 'archived') {
    rowData = archivedQuestions;
  } else if (selectedQuestionId === 'sample') {
    rowData = sampleQuestions;
  }

  // On UI, we only have four types of `answerType`. Since `MULTI_SELECT` is a subtype of `DROP_DOWN`
  // we will convert 'MULTI_SELECT' to 'DROP_DOWN' and then filter the row data accordingly.
  const filteredRowData = rowData
    .map((item) => {
      if (item.answerType === 'MULTI_SELECT') {
        return { ...item, answerType: 'DROP_DOWN' };
      }
      return item;
    })
    .filter((item) => ANSWER_TYPES.review.some((filterItem) => filterItem.value === item.answerType));

  const addNewQuestionHandler = () => {
    dispatch(showQuestionDetails({ questionId: 0, action: 'create', source: 'review', history }));
  };

  const onQuestionChipChange = (id) => {
    setSelectedQuestionId(id[0]);
  };

  const refetchHandler = () => {
    queryClient.invalidateQueries([REACT_QUERY_KEYS.reviewQuestionList.activeQuestions]);
    queryClient.invalidateQueries([REACT_QUERY_KEYS.reviewQuestionList.archivedQuestions]);
  };

  const updateQuestion = (isArchived, data) => {
    const text = isArchived ? 'archive' : 'unarchived';
    saveQuestion({ question: { ...data, isArchived }, source: 'review', buttonType: text })
      .then(() => {
        refetchHandler();
        toast({
          title: `Question ${text} successfully`,
          status: 'success',
        });
      })
      .catch((err) => {
        toast({
          title: err.message,
          status: 'error',
        });
      });
  };

  // const [linkToCopy, setLinkToCopy] = useState('');

  // const menuItems = useMemo(() => {
  //   // Define menu items that are always present
  //   const baseMenuItems = [
  //     {
  //       Component: ({ data }) => {
  //         return <MenuItemComp Icon={OutlineEye} label={'View'} />;
  //       },
  //       action: ({ data }) => {
  //         dispatch(showQuestionDetails({ questionId: data.id, action: 'edit', source: 'review' }));
  //       }
  //     }
  //   ];

  //   let additionalMenuItems = [];

  //   if (selectedQuestionId === 'active') {
  //     additionalMenuItems = [
  //       {
  //         Component: ({ data }) => {
  //           return (
  //             <MenuItemComp Icon={selectedQuestionId === 'active' && Archive} label={'Archive'} />
  //           );
  //         },
  //         action: ({ data }) => {
  //           updateQuestion(true, data);
  //         }
  //       },

  //       {
  //         Component: ({ data }) => {
  //           const host = window.location.protocol + '//' + window.location.host;
  //           const path = getQuestionDetailsRoute({
  //             location: history.location,
  //             questionId: data.id,
  //             action: 'edit',
  //             source: 'review'
  //           });
  //           const linkToCopy = host + path;

  //           const [copied, setCopied] = useState(false);

  //           const { onCopy } = useClipboard(linkToCopy);

  //           const handleCopy = () => {
  //             onCopy();
  //             setCopied(true);
  //             toast({
  //               title: `Link Copied To Clipboard`,
  //               status: 'success'
  //             });
  //           };

  //           return (
  //             <div onClick={handleCopy}>
  //               <MenuItemComp Icon={Share} label={'Copy Link'} />
  //             </div>
  //           );
  //         },
  //         action: ({ data }) => {
  //           // This function doesn't need to return anything, as linkToCopy is calculated in Component
  //         }
  //       },
  //       {
  //         Component: ({ data }) => {
  //           return <MenuItemComp Icon={Clone1} label={'Duplicate'} />;
  //         },
  //         action: ({ data }) => {
  //           dispatch(
  //             showQuestionDetails({ questionId: data.id, action: 'copy', source: 'review' })
  //           );
  //         }
  //       }
  //     ];
  //   } else if (selectedQuestionId === 'archived') {
  //     additionalMenuItems = [
  //       {
  //         Component: ({ data }) => {
  //           return (
  //             <MenuItemComp
  //               Icon={selectedQuestionId === 'archived' && Archive}
  //               label={'Unarchive'}
  //             />
  //           );
  //         },
  //         action: ({ data }) => {
  //           updateQuestion(false, data);
  //         }
  //       }
  //     ];
  //   }

  //   return [...baseMenuItems, ...additionalMenuItems];
  // }, [selectedQuestionId]);

  const columnDefs = useMemo(
    () => [
      {
        flex: 1,
        headerName: 'Question Name',
        width: 500,
        valueGetter: ({ data }) => data?.description,
      },
      {
        sortable: true,
        headerName: 'Answer',
        width: 300,
        cellRenderer: AnswerType,
        filter: 'agSetColumnFilter',
        valueGetter: ({ data }) => data?.answerType,
        filterParams: {
          valueFormatter: (params) =>
            ANSWER_TYPES.review.find((item) => item.value === params.value)?.label || params.value,
        },
      },
      {
        width: 200,
        headerName: 'Type',
        valueGetter: ({ data }) => data?.questionType,
        cellRenderer: QuestionType,
        filter: 'agSetColumnFilter',
        filterParams: {
          valueFormatter: (params) =>
            QUESTION_TYPES.review.find((item) => item.value === params.value)?.label || params.value,
        },
      },
    ],
    []
  );
  const defaultColDef = useMemo(
    () => ({
      headerComponentParams: {
        enableMenu: true,
        enableSorting: true,
      },
      menuTabs: ['filterMenuTab'],
      headerComponent: CustomHeaderCell,
      filter: 'agTextColumnFilter',
      cellStyle: { display: 'flex', alignItems: 'center' },
    }),
    []
  );

  const onRowClicked = useCallback(
    ({ data, event }) => {
      if (event.defaultPrevented) {
        return;
      }
      dispatch(showQuestionDetails({ questionId: data.id, action: 'edit', source: 'review', history }));
    },
    [selectedQuestionId]
  );

  const sideBar = useMemo(
    () => ({
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressColumnFilter: false,
            suppressColumnSelectAll: true,
            suppressColumnExpandAll: true,
          },
        },
      ],
      defaultToolPanel: '',
    }),
    []
  );

  const onArchiveClick = (data) => {
    updateQuestion(true, data);
  };

  const onUnarchiveClick = (data) => {
    updateQuestion(false, data);
  };

  const onCopyLinkClick = (data) => {
    const host = `${window.location.protocol}//${window.location.host}`;
    const path = getQuestionDetailsRoute({
      location: history.location,
      questionId: data.id,
      action: 'edit',
      source: 'review',
    });
    // eslint-disable-next-line no-shadow
    const linkToCopy = host + path;

    const handleCopy = () => {
      navigator.clipboard
        .writeText(linkToCopy)
        .then(() => {
          toast({
            title: `Link Copied To Clipboard`,
            status: 'success',
          });
        })
        .catch(() => {});
    };

    handleCopy();
  };

  const onDuplicateClick = (data) => {
    dispatch(showQuestionDetails({ questionId: data.id, action: 'copy', source: 'review', history }));
  };

  const onViewClick = (data) => {
    dispatch(showQuestionDetails({ questionId: data.id, action: 'edit', source: 'review', history }));
  };

  if (isLoading) {
    return <SkeletonLoader />;
  }

  return (
    <Box
      p={checkNewNavEnabled() ? 3 : '22px 24px'}
      m={checkNewNavEnabled() ? 0 : 4}
      borderRadius="8px"
      bgColor="common.white"
    >
      <Flex mb="12px" justifyContent="space-between">
        <AgGridSearchBar gridRef={gridRef} />
        <Button StartIcon={Plus} onClick={addNewQuestionHandler}>
          Add Question
        </Button>
      </Flex>
      <Flex alignItems="center" gap={2} mb={5}>
        <TextBodyMedium color="text.g3" bold>
          Showing
        </TextBodyMedium>
        <ChipSelect
          options={[
            { id: 'active', text: 'Your Questions' },
            { id: 'archived', text: 'Archived' },
            { id: 'sample', text: 'Sample' },
          ]}
          selectedIds={[selectedQuestionId]}
          onChange={onQuestionChipChange}
        />
      </Flex>
      <AgGridTable
        key={selectedQuestionId}
        ref={gridRef}
        style={{ width: '100%', height: '100%' }}
        rowHeight={70}
        domLayout="autoHeight"
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        rowData={filteredRowData}
        pagination
        rowStyle={{ cursor: 'pointer' }}
        paginationPageSize={10}
        cacheQuickFilter
        onRowClicked={onRowClicked}
        sideBar={sideBar}
        kebabMenuIsRowSpecific
        kebabMenuOptionsGenerator={(data) => {
          const options = generateReviewQuestionBankOptions({
            data,
            onArchiveClick,
            onCopyLinkClick,
            onDuplicateClick,
            onUnarchiveClick,
            onViewClick,
            selectedQuestionId,
          });
          return options;
        }}
        containerAutoHeight
      />
    </Box>
  );
};

export default ReviewQuestionBankV1;
